import React, { useEffect, useState } from 'react';
import { db } from '../firebase/firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import emailjs from 'emailjs-com';

const SecurityDashboard = () => {
  const [claims, setClaims] = useState([]);
  const [items, setItems] = useState([]);
  const [securityInfo, setSecurityInfo] = useState('');
  const [filterStatus, setFilterStatus] = useState('claim requested');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      const code = sessionStorage.getItem('securityCode');
      if (!code) {
        alert('Access denied. Please login.');
        navigate('/security-login');
        return;
      }

      const q = query(collection(db, 'security_officers'), where('code', '==', code));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const officer = snapshot.docs[0].data();
        setSecurityInfo(`${officer.name} (${officer.station})`);
      } else {
        alert('Invalid session. Please login again.');
        sessionStorage.removeItem('securityCode');
        navigate('/security-login');
      }
    };

    checkAccess();
  }, [navigate]);

  useEffect(() => {
    const unsubscribeClaims = onSnapshot(collection(db, 'claims'), (snapshot) => {
      const fetchedClaims = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClaims(fetchedClaims);
    });

    const unsubscribeItems = onSnapshot(collection(db, 'lostItems'), (snapshot) => {
      setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubscribeClaims();
      unsubscribeItems();
    };
  }, []);

  const getItemDescription = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item?.description || '';
  };

  const sendEmailNotification = (claim, status) => {
    const item = items.find(i => i.id === claim.itemId);
    const now = new Date();

    const formatDateTime = (date) =>
      new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'Africa/Nairobi'
      }).format(date);

    const formattedDateTime = formatDateTime(now);

    const userName = claim.claimedByName || claim.yourName || 'User';
    const itemName = item?.itemName || claim.itemName || 'Unnamed Item';
    const foundLocation = claim.location || item?.location || 'Unavailable';
    const originalDescription = claim.itemDescription || item?.description || 'N/A';
    const claimedDescription = claim.claimedDescription || claim.itemDescription || 'N/A';
    const recipientEmail = claim.email || claim.userEmail || item?.userEmail || '';

    if (!recipientEmail) {
      console.warn('No email found for claim:', claim);
      alert('No email address available for this user.');
      return;
    }

    const messageBody = `

Dear ${userName},

Your item claim has been ${status.toUpperCase()} on ${formattedDateTime}.

Please proceed to the IT office to collect it, and remember to bring your student ID for verification.

Item Details:
• Item Name: ${itemName}
• Found Location: ${foundLocation}
• Original Description: ${originalDescription}
• Claimed Description: ${claimedDescription}

Thank you,  
USIU Asset Recovery Team.
`;

    const templateParams = {
      title: `Claim ${status}`,
      to_name: userName,
      name: userName,
      email: recipientEmail,
      to_email: recipientEmail,
      message: messageBody,
      item_name: itemName,
      original_description: originalDescription,
      claimed_description: claimedDescription,
      date_claimed: formattedDateTime,
      status: status
    };

    emailjs.send(
      'service_uqhmv2d',
      'template_ysvizk5',
      templateParams,
      'TXMLkK7adMZgLUrgF'
    ).then(() => {
      alert(`Email sent to ${userName} at ${recipientEmail}`);
    }).catch((error) => {
      console.error('Email sending error:', error);
      alert('Failed to send email.');
    });
  };

  const handleNotify = (claim) => {
    sendEmailNotification(claim, claim.status);
  };

  const handleDelete = async (claimId) => {
    if (window.confirm('Are you sure you want to delete this claim?')) {
      await deleteDoc(doc(db, 'claims', claimId));
      alert('Claim deleted.');
    }
  };

  const buttonStyle = {
    padding: '10px 18px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'white',
    cursor: 'pointer',
    marginRight: '0.5rem'
  };

  const tableHeaderStyle = {
    backgroundColor: '#1B3B6F',
    color: 'white',
    textAlign: 'left',
    padding: '0.75rem'
  };

  const tableRowStyle = {
    backgroundColor: '#f0f4f8',
    padding: '0.75rem',
    borderBottom: '1px solid #ccc'
  };

  return (
    <div style={{ padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
      <h2>Security Dashboard</h2>
      <p>Logged in as: <strong>{securityInfo}</strong></p>

      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="filter" style={{ marginRight: '0.5rem' }}>Filter claims by status:</label>
        <select id="filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="claim requested">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {(filterStatus === 'approved' || filterStatus === 'rejected') && (
        <>
          <h3 style={{
            marginTop: '2rem',
            fontSize: '20px',
            color: filterStatus === 'approved' ? '#2f855a' : '#a94442',
            borderLeft: `5px solid ${filterStatus === 'approved' ? '#2f855a' : '#a94442'}`,
            paddingLeft: '10px'
          }}>
            {filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)} Claims
          </h3>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Item Name</th>
                <th style={tableHeaderStyle}>Claimed By</th>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Original Description</th>
                <th style={tableHeaderStyle}>Claimed Description</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>Notify</th>
                <th style={tableHeaderStyle}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {claims
                .filter(claim => claim.status?.toLowerCase() === filterStatus)
                .sort((a, b) => b.submittedAt?.seconds - a.submittedAt?.seconds)
                .map((claim) => {
                  const actualDesc = getItemDescription(claim.itemId);
                  return (
                    <tr key={claim.id}>
                      <td style={tableRowStyle}>{claim.itemName}</td>
                      <td style={tableRowStyle}>{claim.claimedByName || claim.yourName}</td>
                      <td style={tableRowStyle}>{claim.email || claim.userEmail || 'No email provided'}</td>
                      <td style={tableRowStyle}>{actualDesc}</td>
                      <td style={tableRowStyle}>{claim.claimedDescription || claim.itemDescription}</td>
                      <td style={tableRowStyle}>
                        <span style={{
                          backgroundColor: filterStatus === 'approved' ? '#c6f6d5' : '#f8d7da',
                          color: filterStatus === 'approved' ? '#22543d' : '#721c24',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: 'bold'
                        }}>
                          {filterStatus.toUpperCase()}
                        </span>
                      </td>
                      <td style={tableRowStyle}>
                        <button onClick={() => handleNotify(claim)} style={{ ...buttonStyle, backgroundColor: '#1B3B6F' }}>Notify</button>
                      </td>
                      <td style={tableRowStyle}>
                        <button onClick={() => handleDelete(claim.id)} style={{ ...buttonStyle, backgroundColor: '#D9534F' }}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default SecurityDashboard;
